import { useNavigate } from '@tanstack/react-router';
import { Box } from 'components/Box';
import Icon from 'components/Icon/Icon.component';
import whitePromotionSrc from 'images/icons/bonus_promo_white.svg';
import closeIconSrc from 'images/icons/close-icon-white.svg';
import redHeartIconSrc from 'images/icons/favorite-selected-red.svg';
import whiteHeartIconSrc from 'images/icons/heart-empty.svg';
import whiteUserIconSrc from 'images/icons/user.svg?react';
import { lowerCase } from 'lodash-es';
import React from 'react';
import { useSelector } from 'react-redux';
import { NavbarContent } from 'services/cmsContent/cmsContent.types';
import { selectFavorites } from 'services/favorites/favorites.selectors';
import { selectIsSessionValidated } from 'services/session/session.selectors';
import { selectUserProfile } from 'services/user/user.selectors';
import useGetTranslate from 'utils/hooks/useGetTranslate';
import loyaltyIconSrc from '../icons/loyalty-icon.svg?react';
import {
  CloseButton,
  MenuHeaderWrapper,
  MenuHeading,
  SubHeaderExternalLink,
  SubHeaderInternalLink,
  SubHeaderTappableList,
  SubHeaderTappableListItem,
} from '../style/CompactSlideInMenu.style';

const CompactMenuHeader = React.memo(function CompactMenuHeader({
  navbarContent,
}: {
  navbarContent: NavbarContent;
}) {
  const userProfile = useSelector(selectUserProfile);
  const isAuthenticated = useSelector(selectIsSessionValidated);
  const translate = useGetTranslate();
  const navigate = useNavigate();
  const favorites = useSelector(selectFavorites);

  let headerText: React.ReactNode;

  if (isAuthenticated) {
    headerText = translate('lbl.navbar.mobile.menu.header.signedIn', {
      value: lowerCase(userProfile.name),
    }) as string;
  } else {
    headerText = translate(
      'lbl.navbar.mobile.menu.header.notAuthenticated',
    ) as string;
  }

  const closeDrawer = React.useCallback(() => {
    navigate({ to: '.', hash: '' });
  }, [navigate]);

  return (
    <>
      <MenuHeaderWrapper>
        <MenuHeading>{headerText}</MenuHeading>
        <CloseButton onClick={closeDrawer}>
          <Icon iconSize="16px" iconSrc={closeIconSrc} />
        </CloseButton>
      </MenuHeaderWrapper>
      <SubHeaderTappableList>
        <SubHeaderTappableListItem mobileFlex="1 1 auto">
          <SubHeaderExternalLink
            href={navbarContent.linkPromo}
            target="_self"
            noReferrer={false}
            style={{
              backgroundColor: 'var(--slidein-menu-promo-btn-bg)',
            }}
          >
            <Icon iconSrc={whitePromotionSrc} />
            {navbarContent.labelPromo}
          </SubHeaderExternalLink>
        </SubHeaderTappableListItem>
        {isAuthenticated && (
          <>
            <SubHeaderTappableListItem>
              <SubHeaderExternalLink
                target="_self"
                noReferrer={false}
                href={navbarContent.linkFavorite}
                data-small="true"
              >
                <Icon
                  marginTop={favorites ? '5px' : ''}
                  iconSrc={favorites ? redHeartIconSrc : whiteHeartIconSrc}
                />
              </SubHeaderExternalLink>
            </SubHeaderTappableListItem>
            <SubHeaderTappableListItem>
              <SubHeaderInternalLink to={'/'} data-small="true">
                <Icon svgIconComponent={whiteUserIconSrc} />
              </SubHeaderInternalLink>
            </SubHeaderTappableListItem>
          </>
        )}
      </SubHeaderTappableList>
      <Box marginTop={'8px'}>
        <SubHeaderTappableList>
          {navbarContent.linkLoyalty != null && (
            <SubHeaderTappableListItem mobileFlex="1 1 auto">
              <SubHeaderExternalLink
                href={navbarContent.linkLoyalty}
                target="_self"
                noReferrer={false}
              >
                <Icon svgIconComponent={loyaltyIconSrc} />
                {navbarContent.labelLoyalty}
              </SubHeaderExternalLink>
            </SubHeaderTappableListItem>
          )}
        </SubHeaderTappableList>
      </Box>
    </>
  );
});

export default CompactMenuHeader;
